import React, { useState, useEffect } from 'react';
import VisibilitySensor from 'react-visibility-sensor';

import { Box } from '@styleseat/ui';
import { colors, toRGB } from '@styleseat/brand';
import {
  Button, Stack, HeaderText,
} from '../../../../../ui';
import { InsightCardProps } from '../types';
import { DismissButton } from './DismissButton';
import TimeToComplete from './TimeToComplete';
import MarketingImage from '../../../../PromoteProfile/MarketingImage';
import useAsyncState from '../../../../../../../../packages/hooks/src/useAsyncState';
import {
  shareCurried,
  downloadCurried,
  canShare,
  ShareChannel,
} from '../../../../../../modules/share';
import { useFeatureFlagRedux } from '../../../../../../hooks';
import analytics from '../../../../../../modules/analytics';
// @ts-ignore
import fontUrl from '../../../../../../../styles/fonts/SpaceGrotesk-Bold.woff';

export const BASE_ID = 'marketing-tips-marquee-card';
export const HOLIDAY_INSTAGRAM_ASSETS_SWITCH = 'pro_insight_holiday_IG_asset_fly2986_20211104';

const MarketingAsset1 = require('../../../../../../../images/promote-profile/marketing-asset-1.png');
const MarketingAsset2 = require('../../../../../../../images/promote-profile/marketing-asset-2.png');
const MarketingAsset3 = require('../../../../../../../images/promote-profile/marketing-asset-3.png');
const MarketingAsset4 = require('../../../../../../../images/promote-profile/marketing-asset-4.png');
const MarketingAsset5 = require('../../../../../../../images/promote-profile/marketing-asset-5.png');
const MarketingAsset6 = require('../../../../../../../images/promote-profile/marketing-asset-6.png');
const MarketingAsset7 = require('../../../../../../../images/promote-profile/marketing-asset-7.png');
const MarketingAsset8 = require('../../../../../../../images/promote-profile/marketing-asset-8.png');
const MarketingAsset9 = require('../../../../../../../images/promote-profile/marketing-asset-9.png');

const ShareInstagramCard: React.FC<InsightCardProps> = ({
  id,
  bodyText,
  cta,
  clickThroughData,
  analyticsData,
  onClick,
  onAction,
  firstViewTracking,
  dismissParams,
  timeToComplete,
  metadata,
}) => {
  const [holidayAssetsSwitchEnabled, setHolidayAssetsSwitchEnabled] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);

  const {
    analyticsDismissData,
    onDismiss,
  } = dismissParams;

  const {
    ig_index,
    profileVanityUrl,
    smsText,
    vanitySlug,
  } = metadata;

  const {
    loading: assetsSwitchLoading,
    enabled: assetsSwitchEnabled,
  } = useFeatureFlagRedux(HOLIDAY_INSTAGRAM_ASSETS_SWITCH);

  useEffect(() => {
    if (!assetsSwitchLoading) {
      setHolidayAssetsSwitchEnabled(assetsSwitchEnabled);
      setLoaded(true);
    }
  }, [assetsSwitchLoading]);

  let IGAssets = [
    {
      marketingAsset: MarketingAsset1,
      assetOverlayProps: {
        text: profileVanityUrl,
        offsetBottom: 22,
        textColor: toRGB(colors.punch),
        maxWidth: 23,
      },
    },
    {
      marketingAsset: MarketingAsset2,
      assetOverlayProps: {
        text: smsText,
        offsetBottom: 40,
        textColor: toRGB(colors.blackInBlack),
        maxWidth: 21,
        lineHeight: 10,
        highlightColor: toRGB(colors.punch),
        highlightBlocks: [{
          start: 6,
          end: vanitySlug.length - 1 + 6,
        }, {
          // start is end of above block + "] to "
          start: ((vanitySlug.length - 1) + 6) + 6,
          end: smsText.length - 1,
        }],
      },
    },
    {
      marketingAsset: MarketingAsset3,
      assetOverlayProps: {
        text: profileVanityUrl,
        offsetBottom: 20,
        textColor: toRGB(colors.punch),
        maxWidth: 23,
      },
    },
    {
      marketingAsset: MarketingAsset4,
      assetOverlayProps: {
        text: profileVanityUrl,
        textColor: toRGB(colors.punch),
        offsetBottom: 22,
        fontSize: 10,
        maxWidth: 21,
      },
    },
    {
      marketingAsset: MarketingAsset5,
      assetOverlayProps: {
        text: profileVanityUrl,
        offsetBottom: 10,
        textColor: toRGB(colors.blackInBlack),
        maxWidth: 23,
      },
    },
    {
      marketingAsset: MarketingAsset6,
      assetOverlayProps: {
        text: profileVanityUrl,
        offsetBottom: 66,
        maxWidth: 23,
      },
    },
    {
      marketingAsset: MarketingAsset7,
      assetOverlayProps: {
        text: profileVanityUrl,
        offsetBottom: 15,
        maxWidth: 23,
      },
    },
  ];

  const holidayIGAssets = [
    {
      marketingAsset: MarketingAsset8,
      assetOverlayProps: {
        text: profileVanityUrl,
        offsetBottom: 10,
        textColor: toRGB(colors.punch),
        maxWidth: 23,
      },
    },
    {
      marketingAsset: MarketingAsset9,
      assetOverlayProps: {
        text: profileVanityUrl,
        offsetBottom: 26,
        maxWidth: 23,
      },
    },
  ];

  if (holidayAssetsSwitchEnabled) {
    IGAssets = IGAssets.concat(holidayIGAssets);
  }

  let targetIGIndex = ig_index;
  if (targetIGIndex > (IGAssets.length - 1)) {
    targetIGIndex = 0;
  }
  const targetIGAsset = IGAssets[targetIGIndex].marketingAsset;
  const overlayProps = IGAssets[targetIGIndex].assetOverlayProps;
  const instanceId = `${BASE_ID}-${id}`;
  const [hasTracked, setHasTracked] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const imageWidth = 190;
  const imageHeight = 190;

  // Ensure font is fully loaded so it can be used in the dynamic marketing images Canvas contexts
  const fontFaceLoaded = useAsyncState(async () => {
    if (fontUrl) {
      // @ts-ignore
      const face = new FontFace('SpaceGrotesk', `url(${fontUrl})`, {
        style: 'normal',
        weight: '600',
      });
      await face.load();
      return true;
    }
    return false;
  }, [fontUrl]);

  const visibilityChangeHandler = isVisible => {
    if (hasTracked) {
      return;
    }

    if (isVisible && firstViewTracking) {
      setHasTracked(true);
      firstViewTracking?.();
    }
  };

  const onDownloadImage = useAsyncState(
    async () => {
      const params = {
        target: ShareChannel.None,
        image: imageUrl,
      };
      if (await canShare(params)) {
        // Can open generic share, prefer that over download
        return shareCurried(
          params,
          () => {
            analytics.track('instagram_insight_share_succeeded', {
              asset_index: targetIGIndex + 1,
            });
          },
          () => {
            analytics.track('instagram_insight_share_failed', {
              asset_index: targetIGIndex + 1,
            });
          },
        );
      }
      // Fallback for Desktop browsers + iOS Safari, who can't share images
      return downloadCurried(
        imageUrl,
        () => {
          analytics.track('instagram_insight_download_succeeded', {
            asset_index: targetIGIndex + 1,
          });
        },
        () => {
          analytics.track('instagram_insight_download_failed', {
            asset_index: targetIGIndex + 1,
          });
        },
      );
    }, [imageUrl],
  );

  const getNextIGIndex = () => {
    let nextIGIndex = targetIGIndex + 1;
    if (nextIGIndex > (IGAssets.length - 1)) {
      nextIGIndex = 0;
    }
    return nextIGIndex;
  };

  const handleCtaClick = () => {
    const nextIGIndex = getNextIGIndex();
    onClick(clickThroughData, analyticsData, () => onAction(nextIGIndex));
    onDownloadImage();
  };

  const handleDismissClick = () => {
    onClick(clickThroughData, analyticsDismissData, () => { });
    const nextIGIndex = getNextIGIndex();
    onDismiss(nextIGIndex);
  };

  if (!loaded) return null;

  return (
    <VisibilitySensor onChange={visibilityChangeHandler}>
      <Box
        testID={instanceId}
        color={colors.whiteWash}
        showBoxShadow
        borderRadius={4}
      >
        <Box
          alignItems="center"
          color={colors.lightPurple}
          style={{ width: '100%' }}
        >
          <Box
            paddingY={20}
            alignItems="center"
          >
            <MarketingImage
              assetID={`share_instagram_insight_${targetIGIndex}`}
              image={targetIGAsset}
              width={imageWidth}
              height={imageHeight}
              isFontFaceLoaded={fontFaceLoaded}
              renderedCallback={setImageUrl}
              content={[overlayProps]}
            />
          </Box>
        </Box>
        <Stack
          paddingBottom={16}
          paddingTop={16}
          alignItems="center"
          space={12}
        >
          <TimeToComplete
            testId={`${instanceId}-time-to-complete`}
            time={timeToComplete}
          />
          <Box
            maxWidth="100%"
            paddingTop={4}
            testID={`${instanceId}-text-section`}
          >
            <HeaderText
              level={2}
              textAlign="center"
              testID={`${instanceId}-text`}
            >
              {bodyText?.map(({ color, text }, index) => (
                <HeaderText
                  key={text}
                  level={2}
                  color={color || colors.blackInBlack}
                  textAlign="center"
                >
                  {index > 0 && (' ')}
                  {text}
                </HeaderText>
              ))}
            </HeaderText>
          </Box>
          <Button
            size="small"
            onClick={handleCtaClick}
            testID={`${instanceId}-cta`}
          >
            {cta}
          </Button>
          {dismissParams && (
            <DismissButton
              onDismiss={onDismiss}
              onDismissClick={handleDismissClick}
              analyticsDismissData={analyticsDismissData}
              testID={`${instanceId}-dismiss`}
              dismissText="No Thanks"
            />
          )}
        </Stack>
      </Box>
    </VisibilitySensor>
  );
};

export default ShareInstagramCard;
